import { isEmpty } from 'radash';
import { useCallback, useEffect, useState } from 'react';

import { useToast } from '@/hooks/useToast';
import { PersistedItem } from '@/types/DataModel';

import { useCopyItem, useItem, usePreparedDefaultItem } from '../ItemEditor.queries';
import { ItemOrigin } from '../ItemEditor.types';

export function usePersistedItem({
    category,
    primaryKey,
    originalPrimaryKey,
    templateForItemCopy,
    templateForPreparedDefaultItem,
    fromPreparedDefaultItem,
}: {
    category?: string;
    primaryKey: string;
    originalPrimaryKey: string;
    templateForItemCopy?: object;
    templateForPreparedDefaultItem?: object;
    fromPreparedDefaultItem?: boolean;
}) {
    const { toast } = useToast();

    // Origin is decided by explicit flags rather than by "is the template
    // empty" — an empty template is a legitimate outcome of the prepared
    // default flow (e.g. a category whose customDirective returned {}),
    // and treating it as a plain new item silently drops the user's dialog
    // input. See LAX-10101.
    const itemOrigin: ItemOrigin = !isEmpty(primaryKey)
        ? ItemOrigin.EXISTING
        : !isEmpty(originalPrimaryKey)
            ? ItemOrigin.COPIED
            : (fromPreparedDefaultItem && !isEmpty(category))
                ? ItemOrigin.PREPARED_DEFAULT
                : ItemOrigin.NEW;

    // Existing Item
    const { data: existingItem, isPending: isLoadingExistingItem, refetch: refetchExistingItem } = useItem({
        variables: { primaryKey__: primaryKey },
        enabled: itemOrigin === ItemOrigin.EXISTING,
    });

    // Copied Item
    const [copiedItem, setCopiedItem] = useState<PersistedItem>();
    const { mutate: doCopyItem, isPending: isPendingCopiedItem, isIdle: isLoadingCopiedItem } = useCopyItem({
        onSuccess: (response) => setCopiedItem(response),
        onError: () => toast.error('ERROR_LOADING_ITEM')
    });

    useEffect(() => {
        if (itemOrigin === ItemOrigin.COPIED && isLoadingCopiedItem) {
            doCopyItem({
                primaryKey__: originalPrimaryKey,
                template: templateForItemCopy ? templateForItemCopy : {},
            });
        }
    }, [isLoadingCopiedItem]);

    // Prepared Default Item
    const [preparedDefaultItem, setPreparedDefaultItem] = useState<PersistedItem>();
    const { mutate: doPreparedDefaultItem, isPending: isPendingPreparedDefaultItem, isIdle: isLoadingPreparedDefaultItem } = usePreparedDefaultItem({
        onSuccess: (response) => setPreparedDefaultItem(response),
        onError: () => toast.error('ERROR_LOADING_ITEM')
    });
    useEffect(() => {
        if (itemOrigin === ItemOrigin.PREPARED_DEFAULT && isLoadingPreparedDefaultItem && category) {
            doPreparedDefaultItem({
                category__: category,
                template: templateForPreparedDefaultItem ? templateForPreparedDefaultItem : {},
            });
        }
    }, [isLoadingPreparedDefaultItem]);

    const itemMap: Record<ItemOrigin, [PersistedItem | undefined, boolean]> = {
        [ItemOrigin.EXISTING]: [existingItem, isLoadingExistingItem],
        [ItemOrigin.COPIED]: [copiedItem, isPendingCopiedItem],
        [ItemOrigin.NEW]: [undefined, false],
        [ItemOrigin.PREPARED_DEFAULT]: [preparedDefaultItem, isPendingPreparedDefaultItem],
    };

    const [persistedItem, isLoadingPersistedItem] = itemMap[itemOrigin];

    const refresh = useCallback(() => {
        if (itemOrigin === ItemOrigin.EXISTING) {
            refetchExistingItem();
        } else if (itemOrigin === ItemOrigin.COPIED) {
            doCopyItem({
                primaryKey__: originalPrimaryKey,
                template: templateForItemCopy ? templateForItemCopy : {},
            });
        } else if (itemOrigin === ItemOrigin.PREPARED_DEFAULT && category) {
            doPreparedDefaultItem({
                category__: category,
                template: templateForPreparedDefaultItem ? templateForPreparedDefaultItem : {},
            });
        }
        // For NEW items, there's nothing to refresh
    }, [itemOrigin, refetchExistingItem, doCopyItem, originalPrimaryKey, templateForItemCopy, doPreparedDefaultItem, category, templateForPreparedDefaultItem]);

    return { persistedItem, isLoadingPersistedItem, itemOrigin, refresh };
}
